import { useState, useEffect, useCallback } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const testimonials = [
  {
    name: "Ricardo Almeida",
    role: "CEO, Almeida Investimentos",
    text: "A Transporte Menezes transformou minha rotina de viagens corporativas. Pontualidade impecável, veículos luxuosos e motoristas extremamente profissionais. Não consigo imaginar usar outro serviço.",
    rating: 5,
    avatar: "https://public.readdy.ai/ai/img_res/2aeb4edf2be82b06fcc3c3ec5dd0d527.jpg",
  },
  {
    name: "Fernanda Costa",
    role: "Diretora Executiva, FC Consultoria",
    text: "Uso o serviço há 3 anos para todos os meus transfers de aeroporto. Nunca tive um atraso sequer. A discrição e o profissionalismo da equipe são incomparáveis. Recomendo sem hesitar.",
    rating: 5,
    avatar: "https://readdy.ai/api/search-image?query=professional%20Brazilian%20businesswoman%20portrait%2C%20confident%20smile%2C%20elegant%20attire%2C%20neutral%20dark%20background%2C%20high%20quality%20headshot%20photography&width=200&height=200&seq=test002&orientation=squarish",
  },
  {
    name: "Carlos Mendonça",
    role: "Embaixador, Missão Diplomática",
    text: "Para compromissos diplomáticos, a discrição é fundamental. A Transporte Menezes entende isso perfeitamente. Serviço de classe mundial que atende às mais altas exigências protocolares.",
    rating: 5,
    avatar: "https://readdy.ai/api/search-image?query=distinguished%20Brazilian%20diplomat%20man%20portrait%2C%20formal%20attire%2C%20neutral%20dark%20background%2C%20high%20quality%20headshot%20photography&width=200&height=200&seq=test003&orientation=squarish",
  },
  {
    name: "Isabela Rodrigues",
    role: "Noiva, Casamento dos Sonhos",
    text: "Contratei para o meu casamento e foi perfeito! O carro chegou decorado, o motorista estava impecável e chegamos com toda a elegância que eu sonhava. Momento inesquecível!",
    rating: 5,
    avatar: "https://readdy.ai/api/search-image?query=elegant%20Brazilian%20woman%20portrait%2C%20happy%20smile%2C%20formal%20attire%2C%20neutral%20dark%20background%2C%20high%20quality%20headshot%20photography&width=200&height=200&seq=test004&orientation=squarish",
  },
];

export default function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState<"left" | "right">("right");
  const [visible, setVisible] = useState(true);

  const header = useScrollReveal({ threshold: 0.1 });
  const card = useScrollReveal({ threshold: 0.1 });
  const thumbs = useScrollReveal({ threshold: 0.1 });

  const goTo = useCallback(
    (idx: number, dir: "left" | "right") => {
      if (animating) return;
      setAnimating(true);
      setDirection(dir);
      setVisible(false);

      setTimeout(() => {
        setActive(idx);
        setVisible(true);
        setAnimating(false);
      }, 350);
    },
    [animating]
  );

  const prev = useCallback(() => {
    const idx = (active - 1 + testimonials.length) % testimonials.length;
    goTo(idx, "left");
  }, [active, goTo]);

  const next = useCallback(() => {
    const idx = (active + 1) % testimonials.length;
    goTo(idx, "right");
  }, [active, goTo]);

  // Auto-play
  useEffect(() => {
    const timer = setInterval(() => {
      next();
    }, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const current = testimonials[active];

  const slideClass = visible
    ? "opacity-100 translate-x-0"
    : direction === "right"
    ? "opacity-0 -translate-x-8"
    : "opacity-0 translate-x-8";

  return (
    <section id="testimonials" className="bg-zinc-950 py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          ref={header.ref}
          className={`text-center mb-16 transition-all duration-700 ${
            header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-12 bg-slate-400"></div>
            <span className="text-slate-300 text-sm font-semibold tracking-widest uppercase">
              O Que Dizem
            </span>
            <div className="h-px w-12 bg-slate-400"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Depoimentos de{" "}
            <span className="text-slate-300">Clientes</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            A satisfação dos nossos clientes é o nosso maior troféu.
          </p>
        </div>

        {/* Carousel */}
        <div
          ref={card.ref}
          className={`relative transition-all duration-700 ${
            card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          {/* Card */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden mb-8">
            <div
              className={`flex flex-col lg:flex-row transition-all duration-350 ease-in-out ${slideClass}`}
              style={{ transition: "opacity 0.35s ease, transform 0.35s ease" }}
            >
              {/* Image */}
              <div className="lg:w-72 flex-shrink-0">
                <img
                  src={current.avatar}
                  alt={current.name}
                  className="w-full h-64 lg:h-full object-cover object-top"
                />
              </div>

              {/* Content */}
              <div className="flex-1 p-8 lg:p-12 flex flex-col justify-center">
                <div className="text-slate-500/30 text-8xl font-serif leading-none mb-4">&ldquo;</div>

                <div className="flex gap-1 mb-6 -mt-6">
                  {Array.from({ length: current.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-slate-300 text-lg"></i>
                  ))}
                </div>

                <p className="text-gray-200 text-lg leading-relaxed mb-8 italic">
                  &ldquo;{current.text}&rdquo;
                </p>

                <div>
                  <div className="text-white font-bold text-lg">{current.name}</div>
                  <div className="text-slate-400 text-sm mt-1">{current.role}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation arrows */}
          <button
            onClick={prev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-5 w-10 h-10 flex items-center justify-center bg-zinc-800 hover:bg-slate-400/20 border border-zinc-700 hover:border-slate-400/50 rounded-full text-gray-400 hover:text-slate-300 transition-all duration-300 cursor-pointer"
            aria-label="Anterior"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            onClick={next}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-5 w-10 h-10 flex items-center justify-center bg-zinc-800 hover:bg-slate-400/20 border border-zinc-700 hover:border-slate-400/50 rounded-full text-gray-400 hover:text-slate-300 transition-all duration-300 cursor-pointer"
            aria-label="Próximo"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>

        {/* Dots + Thumbnails */}
        <div
          ref={thumbs.ref}
          className={`flex flex-wrap justify-center gap-4 transition-all duration-700 delay-200 ${
            thumbs.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          {testimonials.map((t, idx) => (
            <button
              key={idx}
              onClick={() => goTo(idx, idx > active ? "right" : "left")}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-all duration-300 cursor-pointer whitespace-nowrap ${
                active === idx
                  ? "border-slate-400 bg-slate-400/10"
                  : "border-zinc-700 bg-zinc-900 hover:border-slate-500/40"
              }`}
            >
              <img
                src={t.avatar}
                alt={t.name}
                className="w-10 h-10 rounded-full object-cover object-top flex-shrink-0"
              />
              <div className="text-left">
                <div className={`text-sm font-semibold ${active === idx ? "text-slate-300" : "text-gray-300"}`}>
                  {t.name}
                </div>
                <div className="text-xs text-gray-500">{t.role.split(",")[0]}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
